import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  InMemoryCache,
  Observable,
  Operation,
  ServerError,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { offsetLimitPagination } from '@apollo/client/utilities';
import axios from 'axios';
import config from 'config';
import { GraphQLError } from 'graphql';

import { ACCESS_TOKEN_KEY } from 'constants/general';

export const authApiClient = axios.create({
  baseURL: config.AUTH_URL,
  withCredentials: true,
});

const httpLink = new BatchHttpLink({
  uri: config.COMMUNITY_URL,
  batchMax: 5,
  batchInterval: 20,
});

const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf(error: ServerError, operation) {
      if (error?.result?.errors?.[0]?.extensions?.code === 'UNAUTHENTICATED') return false;

      return true;
    },
  },
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN_KEY)}`,
    },
  };
});

function refreshTokenOnUnauthenticated(forward: (_: Operation) => Observable<FetchResult>, operation: Operation) {
  // eslint-disable-next-line
  return new Observable<FetchResult<Record<string, any>>>(observer => {
    // used an anonymous function for using an async function
    (async () => {
      try {
        const accessToken = await refreshToken();

        if (!accessToken) {
          throw new GraphQLError('Empty AccessToken');
        }

        // Retry the failed request
        const subscriber = {
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        };

        forward(operation).subscribe(subscriber);
      } catch (err) {
        observer.error(err);
      }
    })();
  });
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions.code === 'UNAUTHENTICATED') {
        return refreshTokenOnUnauthenticated(forward, operation);
      }
    }
  }

  if (networkError) console.log(`[Network error]: ${networkError}`);
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, retryLink.concat(httpLink)]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          posts: offsetLimitPagination(['communityUrl', 'postIds', 'deleted', 'sortBy']),
          members: offsetLimitPagination(['communityId']),
          comments: offsetLimitPagination(['postId', 'parentId', 'deleted']),
          flags: offsetLimitPagination(['filter', 'sortBy']),
        },
      },
    },
  }),
});

const refreshToken = async () => {
  try {
    const {
      data: { accessToken },
    } = await authApiClient.get('/sessions/current');
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken || '');
    return accessToken;
  } catch (err) {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    throw err;
  }
};

export const logOutUser = async () => {
  await authApiClient.delete('/sessions/current');
  localStorage.removeItem(ACCESS_TOKEN_KEY);
};
